import { Show, createSignal, onMount, createEffect } from 'solid-js'
import LoadMoreButton from './buttons/LoadMoreButton'
import ModelPhotoCard from './ModelPhotoCard'
import SubscribeToUnlock from './SubscribeToUnlock'
import { ModelGalleryImage } from '/common/types/aifans-schema'
import { galleryApi } from '/web/store/data/gallery'
import { userStore } from '/web/store/user'
import { isLoggedIn } from '/web/store/api'

interface Props {
  locked: boolean
  images: ModelGalleryImage[]
  onImage: (url: string) => void
  onSubscribe: () => void
  subscribing: boolean
  hasMore: boolean
  setTarget: (el: HTMLElement) => void
  modelName?: string
  creatorName?: string
  backgroundImage?: string
  characterId: string
}

const ModelPhotos = (props: Props) => {
  const [likedImages, setLikedImages] = createSignal<Set<string>>(new Set())
  const [favoritedImages, setFavoritedImages] = createSignal<Set<string>>(new Set())
  const [loading, setLoading] = createSignal<Set<string>>(new Set())
  const [likeCounts, setLikeCounts] = createSignal<Record<string, number>>({})
  const [favoriteCounts, setFavoriteCounts] = createSignal<Record<string, number>>({})

  createEffect(() => {
    const counts: Record<string, number> = {}
    const favCounts: Record<string, number> = {}
    props.images.forEach((img) => {
      counts[img._id] = img.likes
      favCounts[img._id] = img.favorites
    })
    setLikeCounts(counts)
    setFavoriteCounts(favCounts)
  })

  onMount(async () => {
    if (isLoggedIn()) {
      const [likesRes, favsRes] = await Promise.all([
        galleryApi.getLikedImages(),
        galleryApi.getFavoritedImages(),
      ])

      if (likesRes.result?.imageIds) {
        setLikedImages(new Set(likesRes.result.imageIds))
      }
      if (favsRes.result?.imageIds) {
        setFavoritedImages(new Set(favsRes.result.imageIds))
      }
    }
  })

  const toggleLike = async (imageId: string) => {
    if (!isLoggedIn()) {
      userStore.loginModal(true)
      return
    }

    if (loading().has(imageId)) return

    try {
      const newLoading = new Set(loading())
      newLoading.add(imageId)
      setLoading(newLoading)

      if (likedImages().has(imageId)) {
        await galleryApi.unlikeModelImage(imageId)
        const newLiked = new Set(likedImages())
        newLiked.delete(imageId)
        setLikedImages(newLiked)
        setLikeCounts((prev) => ({
          ...prev,
          [imageId]: (prev[imageId] || 0) - 1,
        }))
      } else {
        await galleryApi.likeModelImage(imageId)
        const newLiked = new Set(likedImages())
        newLiked.add(imageId)
        setLikedImages(newLiked)
        setLikeCounts((prev) => ({
          ...prev,
          [imageId]: (prev[imageId] || 0) + 1,
        }))
      }
    } finally {
      const newLoading = new Set(loading())
      newLoading.delete(imageId)
      setLoading(newLoading)
    }
  }

  const toggleFavorite = async (imageId: string) => {
    if (!isLoggedIn()) {
      userStore.loginModal(true)
      return
    }

    if (loading().has(imageId)) return

    try {
      const newLoading = new Set(loading())
      newLoading.add(imageId)
      setLoading(newLoading)

      if (favoritedImages().has(imageId)) {
        await galleryApi.unfavoriteModelImage(imageId)
        const newFavorited = new Set(favoritedImages())
        newFavorited.delete(imageId)
        setFavoritedImages(newFavorited)
        setFavoriteCounts((prev) => ({
          ...prev,
          [imageId]: (prev[imageId] || 0) - 1,
        }))
      } else {
        await galleryApi.favoriteModelImage(imageId)
        const newFavorited = new Set(favoritedImages())
        newFavorited.add(imageId)
        setFavoritedImages(newFavorited)
        setFavoriteCounts((prev) => ({
          ...prev,
          [imageId]: (prev[imageId] || 0) + 1,
        }))
      }
    } finally {
      const newLoading = new Set(loading())
      newLoading.delete(imageId)
      setLoading(newLoading)
    }
  }

  return (
    <>
      <div class="relative md:flex md:flex-col md:items-center">
        {props.locked && (
          <div class="absolute bottom-3 left-2 right-2 top-2 z-10 flex rounded-xl">
            <SubscribeToUnlock
              onSubscribe={props.onSubscribe}
              subscribing={props.subscribing}
              modelName={props.modelName}
              creatorName={props.creatorName}
              backgroundImage={props.backgroundImage}
            />
          </div>
        )}
        <div
          class={`grid grid-cols-2 items-start gap-2.5 sm:grid-cols-3 sm:gap-3.5 lg:grid-cols-4 lg:gap-4 ${
            props.locked ? 'blur-md' : ''
          }`}
        >
          {props.images?.map((image, index) => {
            return (
              <ModelPhotoCard
                id={image._id}
                locked={props.locked}
                liked={likedImages().has(image._id)}
                favorited={favoritedImages().has(image._id)}
                url={image.url}
                onImage={props.onImage}
                onLike={toggleLike}
                onFavorite={toggleFavorite}
                likes={likeCounts()[image._id] ?? image.likes}
                favorites={favoriteCounts()[image._id] ?? image.favorites}
                modelName={props.modelName || ''}
                characterId={props.characterId}
                featured={index === 0}
              />
            )
          })}
        </div>
        <div class="mt-6 md:mt-10">{!props.locked && props.hasMore && <LoadMoreButton />}</div>
      </div>

      <Show when={!props.locked && props.hasMore}>
        <div ref={props.setTarget} class="col-span-full h-10 w-full" />
      </Show>
    </>
  )
}

export default ModelPhotos
